<template>
  <div class="cl__inner">
    <CollectionTopFilters
      v-if="filters && productsCount > 6"
      :filterOptions="filters?.benefit"
      :appliedFilters="appliedFilters?.benefit"
      @onFilterChange="onFilterChange"
    />
    <transition v-if="isMobile">
      <div @click="toggleFilterDrawer" class="mobile-filtering text-bold text-medium-blue">
        Filter products
        <svg class="icon icon--md"><use href="#filter"></use></svg>
      </div>
    </transition>
    <div class="cl__body">
      <div v-if="isFilterDrawerOpen" class="cl__body-left-overlay" @click="toggleFilterDrawer(false)"></div>
      <div :class="['cl__body-left', {'active': isFilterDrawerOpen}]">
        <div class="cl__header">
          <h1 class="cl__title h5-sans">{{ data.title }}</h1>
        </div>

        <div v-if="isFilterDrawerOpen" class="cl__body-left-header text-medium-blue">
          <div class="cl__body-left-header-inner">
            <svg class="cl__arrow-prev hide">
              <use xlink:href="#caret-down"></use>
            </svg>
            <span v-if="data.hide_sort_by === 'true'" class="text-lg text-bold">Filter</span>
            <span v-else class="text-lg text-bold">Filter & Sort</span>
          </div>
          <svg class="cl__arrow-exit icon icon--xl" @click="toggleFilterDrawer(false)">
            <use xlink:href="#x"></use>
          </svg>
        </div>

        <div class="cl__search-container">
          <div class="search-input__wrapper">
            <input
              v-model="tempSearchQuery"
              type="text"
              placeholder="Search"
              class="search-input"
              @keydown.enter="applySearch"
            />
            <svg
              v-if="tempSearchQuery.trim().length > 0"
              class="search-clear caf__close"
              @click="clearSearch"
              @keydown.enter.prevent="clearSearch"
              role="button"
              :aria-label="'Clear search filter'"
              tabindex="0"
            >
              <use xlink:href="#x"></use>
            </svg>
          </div>
          <button class="search-btn btn btn--primary btn-icon icon--sm" @click="applySearch">
            <svg><use href="#search"></use></svg>
          </button>
        </div>

        <div v-if="data.hide_sort_by === 'false'" ref="sortWrapper" class="cl__sort cf__item--active">
          <div class="cl__sort-title-wrapper" @click="toggleSortModule">
            <div class="cl__sort-title text-lg mobile-text-base">Sort by</div>
            <svg :class="['cl__sort-icon']">
              <use xlink:href="#caret-down"></use>
            </svg>
          </div>
          <ul class="cl__sort-items">
            <li v-for="(key, value) in sortOptions" :key="value" class="cl__sort-item">
              <label>
                <input v-model="sortOrder" @change="getSortedProduct" type="radio" name="sort_by" :value="value">
                <span class="text-base">{{value}}</span>
              </label>
            </li>
          </ul>
        </div>
        <CollectionFilters
          v-if="productsCount > 6"
          :filteredProducts="filteredProducts"
          :currentFilter="currentFilter"
          :appliedFilters="appliedFilters"
          :appliedFiltersCount="appliedFiltersCount"
          @on-filter-change="onFilterChange"
          @on-filters-update="onFiltersUpdate"
        />
        <div v-if="appliedFiltersCount > 0 && isFilterDrawerOpen" class="cl__body-left-bottom">
          <div class="caf__clear text-base" @click="clearFilter" tabindex="0" aria-label="Clear All Filters">Clear all</div>
          <button @click="toggleFilterDrawer(false)" class="btn btn--primary cl__apply-filter">Apply Filters</button>
        </div>
      </div>
      <div class="cl__body-right">
        <div class="cl__count-sorting-wrapper">
          <p class="cl__products-count text-base">{{ productCountString }}</p>
          <button @click="toggleFilterDrawer(true)" class="cl__filter-sort-btn">
            <span v-if="data.hide_sort_by === 'true'">Filter</span>
            <span v-else >Filter & Sort</span>
            <svg class="cl__arrow-next">
              <use xlink:href="#caret-down"></use>
            </svg>
          </button>
        </div>
        <!-- Applied Filters Module -->
        <CollectionAppliedFilters
          v-if="appliedFiltersCount > 0"
          :filters="appliedFilters"
          @remove-filter="removeFilter"
          @clear-filter="clearFilter"
        />
        <h5 v-if="!displayProducts.length" class="cl__no-results h5-serif text-center">Sorry, we couldn't find any products matching your search.</h5>
        <div v-if="!productLoader" class="cl__products">
          <template v-for="product in displayProducts" :key="product.handle">   
            <ProductCard
              v-if="product.product_type != 'card'"
              :key="product.id"
              :product="product"
              :showOptionDetails="true"
            />
            <GridCard 
              v-else
              :data="product"
            />
          </template> 
        </div>
        <div v-else class="cph__products">
          <svg v-for="index in 12" :key="index" class="cph__product">
            <use xlink:href="#product-card-loader"></use>
          </svg>
        </div>
        <div v-if="displayProductCount < filteredProducts.length"  class="cl__load-atc-wrapper">
          <button @click="loadMore" class="cl__load-atc mobile-text-sm text-base">
            <span><svg class="icon"><use href="#plus-contained"></use></svg></span>
            <span>Load more items</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, toRefs, ref, onMounted, toRaw, markRaw, watch } from 'vue';
  import ProductCard from "../global/product-card.vue";
  import GridCard from "./components/grid-card.vue";
  import CollectionTopFilters from "./components/collection-top-filters.vue"
  import CollectionFilters from "./components/collection-filters.vue";
  import CollectionFilterModule from "./components/collection-filter-module.vue";
  import CollectionAppliedFilters from "./components/collection-applied-filters.vue";
  import urlSearchParams from './composables/urlSeachParams';
  import useSorting from './composables/useSorting';
  import windowResize from '../../utils/window-resize';

  const props = defineProps({
    data: {
      type: Object,
      default: () => ({})
    }
  })

  const { data } = toRefs(props)
  const collectionData = ref(data.value)
  const displayProductCount = ref(12)
  const productLoader = ref(false)
  const filters = ref(null)
  const currentFilter = ref(null)
  const appliedFilters = ref({})
  const isFilterDrawerOpen = ref(false)
  const sortWrapper = ref(null)
  const { windowWidth } = windowResize()

  const { params, addSearchParam } = urlSearchParams()

  const { sortOptions, sortOrder, sortOrderDisplayNames } = useSorting({
    data: data,
    searchParams: params,
    addSearchParam,
  })

  onMounted(() => {
    filterProducts();
    insertGridCards();
    applyParams();
  })

  const isMobile = computed(() => {
    return windowWidth.value < 1023;
  })

  const productsCount = computed(() => collectionData.value.products.length);

  const productCountString = computed(() => {
    const totalProducts = filteredProducts.value.length;

    if (totalProducts) {
      const itemsString = totalProducts > 1 ? 'items' : 'item';

      return `${totalProducts} ${itemsString}`;
    } else {
      return '';
    }
  });

  const appliedFiltersCount = computed(() =>
    Object.values(appliedFilters.value).reduce((count, val) => count + val.length, 0)
  )

  const tempSearchQuery = ref('');
  const searchQuery = ref('');

  const applySearch = () => {
    searchQuery.value = tempSearchQuery.value;
    window.initYotpoData();
    toggleFilterDrawer();
  };

  const stopwords = new Set(['i', 'want', 'to', 'my', 'the', 'for', 'and', 'or', 'a', 'an', 'in', 'on', 'with', 'by']);

  const vitaminPatterns = {
    'd': 'd3',
    'b': 'b12',
    'k': 'k2',
  };

  function replaceShortcuts(text) {
    const words = text.split(/\s+/);
    return words
      .map(word => {
        if (vitaminPatterns[word]) {
          return vitaminPatterns[word];
        }
        return word;
      })
      .join(' ');
  }

  function normalizeText(text) {
    return text
      .toLowerCase()
      .replace(/[-_]/g, ' ')
      .replace(/[^\w\s]/g, '')
      .trim();
  }

  function extractKeywords(query) {
    if (!query) return [];

    const normalized = normalizeText(query);
    const replaced = replaceShortcuts(normalized);
    const words = replaced.split(/\s+/);

    return [...new Set(
      words.filter(word => 
        word.length > 1 || !stopwords.has(word)
      )
    )];
  }

  function getTagWords(tags) {
    return tags
      .map(tag => {
        const value = tag.includes(':') ? tag.split(':')[1] : tag.includes('=') ? tag.split('=')[1] : tag;
        return normalizeText(value).split(/\s+/);
      })
      .flat()
      .filter(word => word.length > 1);
  }

  function getTitleWords(title) {
    if (!title) return [];
    return normalizeText(title).split(/\s+/).filter(word => word.length > 1);
  }

  function countMatchesInWords(keywords, words) {
    return keywords.filter(keyword => words.includes(keyword)).length;
  }

  function countTotalKeywordMatches(product, keywords) {
    const titleWords = getTitleWords(product.title);
    const tagWords = getTagWords(product.tags);

    const matchesInTitle = countMatchesInWords(keywords, titleWords);
    const matchesInTags = countMatchesInWords(keywords, tagWords);

    return matchesInTitle + matchesInTags;
  }

  const filteredProducts = computed(() => {
    if (appliedFiltersCount.value === 0 && !searchQuery.value) return collectionData.value.products;

    let products = collectionData.value.products.filter(product => product.product_type !== 'card');
    let sortProducts = collectionData.value.products.filter(product => product.product_type !== 'card');

    products = products.filter(product => {
      return Object.entries(appliedFilters.value).every(([key, filterOptions]) => {
        if (!filterOptions.length) return true;
        const tagPrefix = `${key.charAt(0).toUpperCase() + key.slice(1)}:`;
        return product.tags.some(tag => tag.startsWith(tagPrefix) && filterOptions.includes(tag.split(tagPrefix)[1].trim()));
      });
    });

    if (searchQuery.value) {
      const keywords = extractKeywords(searchQuery.value);
      
      if (keywords.length > 0) {
        //console.log('debug Keywords:', keywords);
        products = products.filter(product => {
          const matches = countTotalKeywordMatches(product, keywords);
          //console.log(product.title, 'Matches:', matches);
          return matches;
        });

        
        products = [...products].sort((a, b) => {
          const matchesA = countTotalKeywordMatches(a, keywords);
          const matchesB = countTotalKeywordMatches(b, keywords);
          //console.log('debug Sort:', a.title, 'Matches:', matchesA, b.id, 'Matches:', matchesB);
          return matchesB - matchesA;
        });
      }
    }

    return products;
  });

  const displayProducts = computed(() => {
    const products = toRaw(filteredProducts.value);

    if (!Array.isArray(products)) {
      console.error('displayProducts: invalid filteredProducts', products);
      return [];
    }

    const slicedProducts = products.slice(0, displayProductCount.value);

    return markRaw(slicedProducts);
  });

  const filterProducts = () => {
    const locationTag = `location=${window.shopLocation || 'US'}`;

    collectionData.value.products = collectionData.value.products.filter(product => {
      return product.tags?.includes(locationTag) && product.available;
    });
  }

  const insertGridCards = () => {
    const { products, gridCards } = collectionData.value;
    gridCards.forEach(gridCard => {
      products.splice(gridCard.grid_position - 1, 0, { ...gridCard, product_type: 'card' });
    });
  };

  const applyParams = () => {
    if (!params.value) {
      return;
    }
    
    // Sorting Params
    if (Object.entries(params.value).some(([key]) => key.startsWith('sort-order'))) {
      getSortedProduct();
    }
  };

  const getSortedProduct = async (method) => {
    productLoader.value = true;
    try {
      const response = await fetch(`/collections/${data.value.handle}?view=json&sort_by=${sortOptions[sortOrder.value]}`);
      const newData = await response.json();
      collectionData.value = newData;
    } finally {
      filterProducts();
      insertGridCards();
      productLoader.value = false;
    }
  };

  // const applySort = (method) => {
  //   const { products } = collectionData.value;
  //   if (method == 'manual') {
  //     getSortedProduct(method);
  //   } else if (method == 'best-selling') {
  //     getSortedProduct(method);
  //   } else if (method == 'price-ascending') {
  //     products.sort(sortBy('max_price', 'ascending'));
  //   } else if (method == 'price-descending') {
  //     products.sort(sortBy('max_price', 'descending'));
  //   } else if (method == 'created-ascending') {
  //     products.sort(sortBy('created_at', 'ascending'));
  //   }
  // }

  // const sortBy = (prop, direction) => {
  //   return function(a, b) {
  //     var a_prop = typeof prop != 'undefined' ? a[prop] : a;
  //     var b_prop = typeof prop != 'undefined' ? b[prop] : b;

  //     if (direction == 'descending') {
  //       return a_prop > b_prop ? -1 : b_prop > a_prop ? 1 : 0;
  //     } else {
  //       return a_prop > b_prop ? 1 : b_prop > a_prop ? -1 : 0;
  //     }
  //   }
  // }

  const toggleSortModule = () => {
    sortWrapper.value.classList.toggle('cf__item--active')
  }
  
  const toggleFilterDrawer = (value) => {
    isFilterDrawerOpen.value = value;
    document.body.style.overflow = value ? 'hidden' : 'auto';
  };

  const onFilterChange = (filterKey, options) => {
    currentFilter.value = options.length ? filterKey : '';
    appliedFilters.value = { ...appliedFilters.value, [filterKey]: options };
  };

  const onFiltersUpdate = (value) => {
    filters.value = { ...value };
  }

  const removeFilter = (filterKey, option) => {
    appliedFilters.value[filterKey] = appliedFilters.value[filterKey].filter(filter => filter !== option);
    currentFilter.value = appliedFilters.value[filterKey].length ? filterKey : '';
    window.initYotpoData();
  };

  const clearFilter = () => {
    appliedFilters.value = {}
    currentFilter.value = ''
    window.initYotpoData()
  }

  const clearSearch = () => {
    tempSearchQuery.value = ''
    applySearch()
    window.initYotpoData()
  }

  const loadMore = () => {
    displayProductCount.value = Math.min(displayProductCount.value + 12, filteredProducts.value.length)
    window.initYotpoData()
  };
</script>